/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  styled,
  TextField,
  Button,
  IconButton,
  Collapse,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, PictureAsPdf, TableChart } from '@mui/icons-material';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Table from '@Components/table';
import { subareasByCustomerColumns } from './columns';
import { useGetCustomersQuery, useGetSubareasByCustomerQuery, useLazyGetCustomersSelectionsQuery } from '@Store/api/customersSlice';
import { ICustomerSelectionReport } from '@Types/customerType';
import { exportData } from '@Utils/export-csv';
import { handleFetchError } from '@Utils/handleError';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ToolbarContainer = styled(Box)`
  padding: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

// Redundant inside a customer's own detail table - the customer is already the parent row.
const detailColumns = subareasByCustomerColumns.filter((column) => column.field !== 'customerName');

interface ICustomerSummary {
  id: string;
  name: string;
}

function CustomerDetailRow({ customer }: { customer: ICustomerSummary }) {
  const [expanded, setExpanded] = useState(false);

  const { data: subareasReport, isFetching, error } = useGetSubareasByCustomerQuery(customer.id, {
    skip: !expanded,
  });

  useEffect(() => {
    if (expanded) handleFetchError(error);
  }, [error, expanded]);

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 56 }}>
          <IconButton aria-label='expand row' onClick={() => setExpanded((value) => !value)} size='small'>
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component='th' scope='row'>
          {customer.name}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box sx={{ my: 2 }}>
              {isFetching ? (
                <Box display='flex' justifyContent='center' p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Table
                  columns={detailColumns}
                  paginated={false}
                  rows={subareasReport?.items ?? []}
                  totalCount={subareasReport?.count}
                />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function SubareasByCustomer() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const customersParams = useMemo(
    () => ({
      pageSize,
      currentPage: page + 1,
      search: debouncedSearch,
    }),
    [page, pageSize, debouncedSearch]
  );

  const { data: customersReport, isFetching: isFetchingCustomers, error: customersError } =
    useGetCustomersQuery(customersParams);
  const customers = customersReport?.items ?? [];

  const [getAllCustomersSelections] = useLazyGetCustomersSelectionsQuery();

  useEffect(() => {
    handleFetchError(customersError);
  }, [customersError]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  // Both exports flatten every customer's every subarea into one file (customer-as-a-column),
  // independent of the on-screen master-detail grouping/pagination. Availability/Zone Info come
  // back blank here - see CustomerService.EnrichSelectionsWithOccupancy - since this is an
  // unfiltered, all-customers fetch and occupancy is only ever looked up for one customer at a
  // time (matching what happens when you expand a single customer's row on screen).
  const fetchAllSubareas = async (): Promise<ICustomerSelectionReport[]> => {
    const all = await getAllCustomersSelections({ getAllPages: true } as any).unwrap();

    return all?.items ?? [];
  };

  const handleExportCsv = async () => {
    const source = await fetchAllSubareas();

    const csv = Papa.unparse({
      fields: subareasByCustomerColumns.map((column) => column.headerName!),
      data: source.map((row) => subareasByCustomerColumns.map((column) => column.valueGetter!({ row } as any))),
    });

    exportData(csv);
  };

  const handleExportPdf = async () => {
    const source = await fetchAllSubareas();

    const doc = new jsPDF({ compress: true, orientation: 'landscape' });

    doc.setFontSize(14);
    doc.text('Subareas By Customer', 14, 15);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 21);

    autoTable(doc, {
      startY: 26,
      head: [subareasByCustomerColumns.map((column) => column.headerName!)],
      body: source.map((row) =>
        subareasByCustomerColumns.map((column) => String(column.valueGetter!({ row } as any) ?? ''))
      ),
      theme: 'grid',
      styles: { fontSize: 7 },
    });

    doc.save('subareas-all-customers.pdf');
  };

  return (
    <Container id='subareas-by-customer-container'>
      <ToolbarContainer>
        <TextField
          label='Search Customers'
          onChange={(event) => setSearchInput(event.target.value)}
          size='small'
          sx={{ minWidth: 260 }}
          value={searchInput}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={handleExportCsv} startIcon={<TableChart />} variant='outlined'>
          Export CSV
        </Button>
        <Button onClick={handleExportPdf} startIcon={<PictureAsPdf />} variant='outlined'>
          Export PDF
        </Button>
      </ToolbarContainer>

      <TableContainer component={Paper} sx={{ m: 2, width: 'auto' }}>
        <MuiTable>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }} />
              <TableCell>Customer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetchingCustomers ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ py: 4, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : null}
            {!isFetchingCustomers && customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color='text.secondary'>No customers found</Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {!isFetchingCustomers &&
              customers.map((customer) => (
                <CustomerDetailRow customer={{ id: customer.id, name: customer.name }} key={customer.id} />
              ))}
          </TableBody>
        </MuiTable>
        <TablePagination
          component='div'
          count={customersReport?.count ?? 0}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setPage(0);
          }}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Container>
  );
}
